import { Form, FormikProvider, useFormik } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap'
import { useDebouncedCallback } from 'use-debounce'
import * as Yup from 'yup'
import { DETAIL_SEARCH_TYPE } from '../../../common/constants'
import { clearDetailSearchCriteria, detailSearchDefaultCriteria, setDetailSearchCriteria } from '../../../store/search'
import { SearchButton } from '../../Common/Buttons'
import Loader from '../../Common/Loader'
import RecursiveForm from '../../RecursiveForm'
import { useDetailSearch } from '../hooks/useDetailSearch'
import { useDetailSearchCount } from '../hooks/useDetailSearchCount'
import { DetailSearchType, getDetailSearchConfig } from '../utils/detailSearchConfig'

const getModalSize = (type: string) => {
    switch (type) {
        case DETAIL_SEARCH_TYPE.PROPERTY:
        case DETAIL_SEARCH_TYPE.CONTACT:
        case DETAIL_SEARCH_TYPE.COMPANY:
            return 'xl'
        case DETAIL_SEARCH_TYPE.PROJECT:
        case DETAIL_SEARCH_TYPE.DOCUMENT:
            return 'lg'
        case DETAIL_SEARCH_TYPE.INTERMEDIARY_DIARY:
        case DETAIL_SEARCH_TYPE.EVIDENCE_DIARY:
            return 'md'
        default:
            return 'lg'
    }
}

interface DetailSearchModalProps {
    htmlId?: string
    isOpen: boolean
    type: DetailSearchType
    toggle: () => void
}

const DetailSearchModal = ({ htmlId, isOpen, type, toggle }: DetailSearchModalProps) => {
    const dispatch = useDispatch()
    const history = useHistory()
    const { t } = useTranslation()

    const [debouncedCriteria, setDebouncedCriteria] = useState<Record<string, unknown> | null>(null)

    const size = getModalSize(type)

    const { selector } = getDetailSearchConfig(type)
    const searchCriteria = useSelector(selector)
    const hasStoredCriteria = searchCriteria !== detailSearchDefaultCriteria[type]

    const {
        config,
        title,
        subtitle,
        inputs,
        profileInputs,
        validationConfig,
        profileValidationConfig,
        selectOptions,
        profileSelectOptions,
        selectOptionsLoading,
        redirectPage,
    } = useDetailSearch(type, searchCriteria)

    const formGroup = validationConfig.formGroup ?? ''
    const profileFormGroup = profileValidationConfig?.formGroup ?? ''

    const onDetailSearchChange = useDebouncedCallback((values: Record<string, unknown>) => {
        const submitValues = config.transformCriteria(values)
        setDebouncedCriteria(submitValues)
    }, 500)

    const { data: countData, isLoading: loadingCount } = useDetailSearchCount({
        type,
        config,
        criteria: debouncedCriteria ?? {},
        enabled: isOpen && debouncedCriteria !== null,
    })

    const count = countData?.count ?? 0

    const initialValues = profileValidationConfig
        ? {
              [formGroup]: validationConfig.initialValues ?? {},
              [profileFormGroup]: profileValidationConfig.initialValues ?? {},
          }
        : (validationConfig.initialValues ?? {})

    const validationSchema = profileValidationConfig
        ? Yup.object({
              [formGroup]: validationConfig.validationSchema ?? Yup.object({}),
              [profileFormGroup]: profileValidationConfig.validationSchema ?? Yup.object({}),
          })
        : (validationConfig.validationSchema ?? Yup.object({}))

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: (values) => {
            const submitValues = config.transformCriteria(values)

            dispatch(setDetailSearchCriteria(type, submitValues))

            toggle()

            history.push({ pathname: `/${redirectPage}` })
        },
    })

    // Skips the count fetch for a fresh, untouched, never-searched-before form (no default criteria, no prior search, no
    // user input yet) — fetching a count for it would just be "all items", which isn't meaningful to show.
    useEffect(() => {
        if (!formik.dirty && !config.hasInitialCriteria && !hasStoredCriteria) return

        onDetailSearchChange(formik.values as Record<string, unknown>)
    }, [formik.values, formik.dirty, config.hasInitialCriteria, hasStoredCriteria, onDetailSearchChange])

    const clearDetailSearch = () => {
        dispatch(clearDetailSearchCriteria(type))
        setDebouncedCriteria(null)
        formik.resetForm()
    }

    return (
        <>
            <Modal size={size} isOpen={isOpen} id={htmlId} toggle={toggle} centered>
                <ModalHeader toggle={toggle}>{title}</ModalHeader>
                <ModalBody>
                    <FormikProvider value={formik}>
                        <Form onSubmit={formik.handleSubmit}>
                            <RecursiveForm
                                config={inputs?.config}
                                inputs={inputs.config ? inputs.columns : inputs?.mainForm}
                                formik={formik}
                                selectOptions={selectOptions}
                                selectOptionsLoading={selectOptionsLoading}
                                inputMargin="mb-2"
                                subform={profileValidationConfig ? formGroup : ''}
                                {...({} as any)} // TODO: temporaty fix since RecursiveForm is not typed properly
                            />

                            {profileInputs && (
                                <>
                                    <h5 className="mt-3 mb-4 fs-14 fw-normal">{subtitle}</h5>

                                    <RecursiveForm
                                        config={profileInputs?.config}
                                        inputs={profileInputs.config ? profileInputs.columns : profileInputs?.mainForm}
                                        formik={formik}
                                        selectOptions={profileSelectOptions}
                                        selectOptionsLoading={selectOptionsLoading}
                                        inputMargin="mb-2"
                                        subform={profileFormGroup}
                                        {...({} as any)} // TODO: temporaty fix since RecursiveForm is not typed properly
                                    />
                                </>
                            )}

                            <div className="d-flex justify-content-end align-items-center mb-3 mt-2">
                                <div className="me-4 d-flex align-items-center">
                                    {loadingCount && <Loader />}
                                    {t('form.detailSearch.results')}
                                    <span className="fw-medium mx-1">{count}</span>
                                </div>
                                <SearchButton type="submit" />
                            </div>
                            <div className="d-flex justify-content-end align-items-center">
                                <Button
                                    color="link"
                                    className="d-flex align-items-center text-danger text-decoration-none p-0"
                                    onClick={clearDetailSearch}
                                >
                                    <i className="mdi mdi-trash-can-outline me-1 fs-17"></i> {t('button.cancelSearch')}
                                </Button>
                            </div>
                        </Form>
                    </FormikProvider>
                </ModalBody>
            </Modal>
        </>
    )
}

export default DetailSearchModal
